import {Product} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/product.types';
import {SizeSelection} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/size_selection.types';
import {
  ButtonTabs,
  COLORS,
  ModalDialog,
  ModalDialogCloseButton,
  SimpleTabs,
  SizeBubbleList,
  SoldOutInfoHeader,
} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React, {FC} from 'react';
import {SizeReminder} from '../SizeReminder/SizeReminder.lazy';
import {SizeDropdownList} from './SoldoutDropdowntList';
import {useSizeReminderPopupOpenedState} from './hooks/useSizeReminderPopupOpenedState';

export interface SoldOutSizeSelectionProps {
  sizeSelection: SizeSelection;
  onSizeSelect: (id: number) => void;
  selectedSizeIds: number[];
  product: Product;
  sizeReminderSizeIds: number[];
  resetSizeSelection: () => void;
  renderInPortal?: boolean;
  renderAsDropdown?: boolean;
}

export const SoldOutSizeSelectionModal: FC<SoldOutSizeSelectionProps> = ({
  sizeSelection,
  selectedSizeIds,
  onSizeSelect,
  product,
  sizeReminderSizeIds,
  resetSizeSelection,
  renderInPortal = true,
}) => {
  const {cancel, isOpen} = useSizeReminderPopupOpenedState();

  const {type} = sizeSelection;

  const handleSubmissionProcessed = () => {
    cancel();
  };

  if (!type) {
    return null;
  }

  return (
    <ModalDialog
      renderInPortal={renderInPortal}
      contentFitWidth
      open={isOpen}
      onClose={cancel}
      data-testid="soldOutSizeReminderPopup"
      bodyContent={
        <>
          <StyledModalDialogCloseButton
            onClick={cancel}
            data-testid="soldOutSizeReminderPopupCloseIcon"
          />
          <SoldOutInfoHeader
            productImage={product.primaryBustImage}
            hasColorOptions={Boolean(product && product.colors.length > 0)}
          />
          <SoldOutSizeReminderForm
            cancel={cancel}
            handleSubmissionProcessed={handleSubmissionProcessed}
            onSizeSelect={onSizeSelect}
            product={product}
            selectedSizeIds={selectedSizeIds}
            sizeReminderSizeIds={sizeReminderSizeIds}
            sizeSelection={sizeSelection}
            resetSizeSelection={resetSizeSelection}
            hasBackButton
          />
        </>
      }
    />
  );
};

interface SoldOutReminderFormProps extends SoldOutSizeSelectionProps {
  cancel?: () => void;
  handleSubmissionProcessed?: () => void;
  hasBackButton?: boolean;
}
export const SoldOutSizeReminderForm: React.FC<SoldOutReminderFormProps> = ({
  onSizeSelect,
  selectedSizeIds,
  sizeReminderSizeIds,
  sizeSelection,
  product,
  handleSubmissionProcessed,
  cancel,
  hasBackButton = false,
  renderAsDropdown,
  resetSizeSelection,
}) => {
  const {type} = sizeSelection;
  if (!type) return null;

  const handleSubmission = () => {
    resetSizeSelection();
    handleSubmissionProcessed && handleSubmissionProcessed();
  };

  return (
    <SizeReminder
      onBackButtonClick={() => cancel && cancel()}
      onSubmissionProcessed={handleSubmission}
      productId={product.id}
      selectedSizeIds={sizeReminderSizeIds}
      hasBackButton={hasBackButton}
    >
      <SizeSelectionContainer>
        {type.$case === 'oneSize' && type.oneSize.size && (
          <SizeBubbleList
            sizes={[type.oneSize.size]}
            selectedSizeIds={selectedSizeIds}
            onSizeSelect={onSizeSelect}
          />
        )}
        {type.$case === 'inlineSelection' && (
          <SizeBubbleList
            sizes={type.inlineSelection.sizes}
            selectedSizeIds={selectedSizeIds}
            onSizeSelect={onSizeSelect}
          />
        )}
        {type.$case === 'sizes' && (
          <>
            {renderAsDropdown ? (
              <SizeDropdownList
                sizes={type.sizes.sizes}
                selectedSizeIds={selectedSizeIds}
                onSizeSelect={onSizeSelect}
              />
            ) : (
              <SizeBubbleList
                sizes={type.sizes.sizes}
                selectedSizeIds={selectedSizeIds}
                onSizeSelect={onSizeSelect}
              />
            )}
          </>
        )}
        {type.$case === 'sizeRuns' && (
          <>
            {renderAsDropdown ? (
              <SizeDropdownList
                sizeRuns={type.sizeRuns.sizeRuns}
                selectedSizeIds={selectedSizeIds}
                onSizeSelect={onSizeSelect}
              />
            ) : (
              <StyledSimpleTabs>
                {type.sizeRuns.sizeRuns.map((sizeRun, index) => {
                  return (
                    <SizeBubbleList
                      label={sizeRun.label}
                      sizes={sizeRun.sizes}
                      selectedSizeIds={selectedSizeIds}
                      onSizeSelect={onSizeSelect}
                      key={index}
                    />
                  );
                })}
              </StyledSimpleTabs>
            )}
          </>
        )}

        {type.$case === 'groupedSizes' && (
          <ButtonTabs>
            {type.groupedSizes.groups.map((group, index) => {
              return (
                <>
                  {renderAsDropdown ? (
                    <SizeDropdownList
                      groupedSizes={type.groupedSizes.groups}
                      selectedSizeIds={selectedSizeIds}
                      onSizeSelect={onSizeSelect}
                    />
                  ) : (
                    <Group label={group.label} key={index}>
                      {group.content?.$case === 'sizes' && (
                        <SizeBubbleList
                          sizes={group.content.sizes.sizes}
                          selectedSizeIds={selectedSizeIds}
                          onSizeSelect={onSizeSelect}
                        />
                      )}
                      {group.content?.$case === 'sizeRuns' && (
                        <StyledSimpleTabs>
                          {group.content.sizeRuns.sizeRuns.map(
                            (sizeRun, index) => {
                              return (
                                <SizeBubbleList
                                  label={sizeRun.label}
                                  sizes={sizeRun.sizes}
                                  selectedSizeIds={selectedSizeIds}
                                  onSizeSelect={onSizeSelect}
                                  key={index}
                                />
                              );
                            },
                          )}
                        </StyledSimpleTabs>
                      )}
                    </Group>
                  )}
                </>
              );
            })}
          </ButtonTabs>
        )}
      </SizeSelectionContainer>
    </SizeReminder>
  );
};

const StyledModalDialogCloseButton = styled(ModalDialogCloseButton)`
  position: absolute;
  right: 0;
  top: 0;
  &&& {
    padding: 24px;
  }
`;

const SizeSelectionContainer = styled.div`
  margin-bottom: 10px;
`;

const StyledSimpleTabs = styled(SimpleTabs)`
  > button {
    color: ${COLORS.Black};
  }
`;

const Group = styled.div<{label?: string}>``;
